<template>
  <OmtLayoutContainer>
    <OmtRow mb="24px">
      <OmtTypography as="h1-bold"> Adicione os exames </OmtTypography>
    </OmtRow>

    <OmtRow mb="24px">
      <OmtInput
        variant="filled"
        iconRight="search"
        placeholder="Busque pelo nome do exame"
        v-model="search"
        :iconRightClick="onSearch"
        v-on:keydown.enter.prevent="onSearch"
      />
    </OmtRow>

    <OmtRow class="full-view-swipe" mb="40px">
      <OmtSwiper>
        <OmtSwiperSlide v-for="(item, index) in terms" :key="index" @click="onTermSelected(item.label)">
          <OmtButton :variant="item.isActive ? 'solid' : 'outline-gray'" min-width="62px" height="42px">
            <OmtTypography as="button-small" :color="item.isActive ? 'white' : 'var(--colors-light-primary-gray1)'">
              {{ item.label }}
            </OmtTypography>
          </OmtButton>
        </OmtSwiperSlide>
      </OmtSwiper>
    </OmtRow>

    <OmtRow mb="24px">
      <OmtTypography as="h2-bold">
        {{ isSearching ? "Resultados" : "Exames mais procurados" }}
      </OmtTypography>
    </OmtRow>

    <OmtRow stack gap="16px" mb="42px">
      <OmtCardTile
        v-for="(item, index) in searchedExams"
        :key="index"
        :icon="{ name: 'clipboard-list-check', size: 'lg' }"
        :title="item.name"
        @click="selectExam(item)"
        :border-color="
          item.isActive ? 'var(--colors-light-secondary-light-blue)' : 'var(--colors-light-secondary-divider)'
        "
      >
        <template v-slot:lead>
          <CBox @click.stop="selectExam(item)">
            <OmtIcons :name="item.isActive ? 'minus' : 'plus'" size="lg" />
          </CBox>
        </template>
      </OmtCardTile>
    </OmtRow>

    <OmtRow :desktopStart="5" :desktopEnd="8" bottomFixed>
      <ButtonCounter
        :count="selectedExams.length"
        :disabled="!selectedExams.length"
        @click="$router.push({ path: '/copay/simulations/create/review' })"
        label="Ver exames"
      />
    </OmtRow>

    <OmtStickedModal v-if="showInformativeModal" @close="closeModals">
      <OmtTypography as="h1-bold" mb="14px"> {{ selectedExam.name }} </OmtTypography>
      <OmtTypography as="p1"> {{ selectedExam.description }} </OmtTypography>

      <template #footer>
        <OmtButton w="100%" @click="addExam"> Adicionar </OmtButton>
      </template>
    </OmtStickedModal>

    <CheckboxGroupModal
      v-if="showCheckboxModal"
      @close="closeModals"
      :options="selectedExam.options"
      :title="selectedExam.name"
      @save="saveCheckboxModal"
    />
  </OmtLayoutContainer>
</template>

<script>
import { mapActions, mapState } from "vuex";
import OmtCardTile from "@/components/Molecules/CardTile";
import CheckboxGroupModal from "@/components/Molecules/CheckboxGroupModal";
import ButtonCounter from "@/components/Molecules/ButtonCounter";

export default {
  components: {
    OmtCardTile,
    CheckboxGroupModal,
    ButtonCounter
  },
  data() {
    return {
      showInformativeModal: false,
      showCheckboxModal: false,
      isSearching: false,
      selectedExam: {},
      search: ""
    };
  },
  computed: {
    ...mapState("copay", ["mostSearchedExamsTerms", "selectedExams", "searchedExams"]),
    terms() {
      return this.mostSearchedExamsTerms.map(t => ({
        label: t,
        isActive: (t === "Todos" && !this.isSearching) || t === this.search
      }));
    }
  },
  async mounted() {
    try {
      this.$root.$emit("show-loading");
      await this.fetchMostSearchedExams();
    } catch (error) {
      console.error("copay exam error", error);
    } finally {
      this.$root.$emit("hide-loading");
    }
  },
  methods: {
    ...mapActions("copay", [
      "fetchMostSearchedExams",
      "searchExam",
      "triggerExam",
      "searchExamGroup",
      "searchExamSynonym"
    ]),
    closeModals() {
      this.showInformativeModal = false;
      this.showCheckboxModal = false;
    },
    showSuccessToast() {
      this.$toast({
        description: "Exame adicionado com sucesso",
        status: "success",
        duration: 2000,
        position: "top"
      });
    },
    addExam() {
      if (!this.selectedExam?.id) return;
      this.triggerExam({
        isActive: true,
        id: this.selectedExam.id,
        name: this.selectedExam.name,
        group: this.selectedExam.group
      });
      this.showSuccessToast();
      this.showInformativeModal = false;
      this.selectedExam = {};
    },
    saveCheckboxModal(options) {
      if (!options?.length) return;
      options.forEach(s =>
        this.triggerExam({ isActive: s.isActive, id: s.id, name: s.label, group: this.selectedExam.group })
      );
      this.showSuccessToast();
      this.showCheckboxModal = false;
      this.selectedExam = {};
    },
    async selectExam(item) {
      if (item.isActive) {
        if (item.options?.length) {
          item.options.forEach(o => {
            this.triggerExam({ isActive: false, id: o.id });
          });
          return;
        }
        this.triggerExam({ isActive: false, id: item.id });
        return;
      }
      if (item.group) {
        if (!item.options?.length) {
          try {
            this.$root.$emit("show-loading");
            const newItem = await this.searchExamGroup({ exam: item });
            if (newItem?.options?.length) item = newItem;
          } catch (error) {
            console.error("copay exam error", error);
          } finally {
            this.$root.$emit("hide-loading");
          }
        }
        if (item.options?.length) {
          item = JSON.parse(JSON.stringify(item));
          item.options.forEach(o => {
            if (o.id === item.id) {
              o.isActive = true;
            }
          });
          this.selectedExam = item;
          this.showCheckboxModal = true;
          return;
        }
      }
      if (item.hasSynonym) {
        if (!item.description) {
          try {
            this.$root.$emit("show-loading");
            const newItem = await this.searchExamSynonym({ exam: item });
            if (newItem?.description) item = newItem;
          } catch (error) {
            console.error("copay exam error", error);
          } finally {
            this.$root.$emit("hide-loading");
          }
        }
        if (item.description) {
          this.selectedExam = item;
          this.showInformativeModal = true;
          return;
        }
      }
      this.triggerExam({ isActive: true, id: item.id, name: item.name, group: item.group });
      this.showSuccessToast();
    },
    onTermSelected(item) {
      this.search = item === "Todos" ? "" : item;
      this.onSearch();
    },
    async onSearch() {
      try {
        this.$root.$emit("show-loading");
        this.isSearching = this.search !== "";
        await this.searchExam({ search: this.search });
      } catch (error) {
        console.error("copay exam error", error);
      } finally {
        this.$root.$emit("hide-loading");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.full-view-swipe {
  margin-left: -16px;
  min-width: calc(100% + 32px);

  ::v-deep .swiper {
    padding: 0 16px;
  }

  ::v-deep .swiper-slide {
    width: auto;
  }

  button {
    pointer-events: none;
  }
}

@media screen and (min-width: 1024px) {
  .full-view-swipe {
    margin-left: -36px;
    min-width: calc(100% + 72px);
  }
}
</style>
